import {
  Button,
  Switch,
  type AlertColor,
  type AlertPropsColorOverrides,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  DataGrid,
  GridAddIcon,
  type GridColDef,
  type GridRenderCellParams,
  type GridTreeNodeWithRender,
} from '@mui/x-data-grid';

import PageBaseLayout from '../layouts/PageBaseLayout';
import { Link, NavLink, Outlet } from 'react-router';
import { useContext, useEffect, useState } from 'react';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import { supabase } from '../supabase-client';
import type { Campaign } from '../models/types';
import useDialog from '../hooks/useDialog';
import DeleteCampaignDialog from '../components/DeleteCampaignDialog';
import { AuthContext } from '../contexts/AuthContext';
import type { OverridableStringUnion } from '@mui/types';

export default function BrandAnalytics() {
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const authCtx = useContext(AuthContext);

  const columns: GridColDef<Campaign>[] = [
    {
      field: 'name',
      headerName: 'Campaign Name',
      width: 200,
      renderCell: (param) => (
        <Button
          component={NavLink}
          to={`./${param.row.id}`}
          state={param.row}
          size="small"
          variant="outlined">
          {param.value}
        </Button>
      ),
    },
    {
      field: 'affiliate_link_count',
      headerName: 'Affiliate Links',
      type: 'number',
    },
    {
      field: 'voucher_redemption_count',
      headerName: 'Voucher Redemptions',
      type: 'number',
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      width: 100,
      type: 'string',
    },
    {
      field: 'minimum_spend',
      headerName: 'Min. Spend',
      width: 100,
      type: 'number',
    },
    {
      field: 'affiliate_earning',
      headerName: 'Affiliate Earning',
      width: 100,
      type: 'number',
    },
    {
      field: 'referee_benefit_type',
      headerName: 'Referee Benefit Type',
      width: 100,
      type: 'string',
    },
    {
      field: 'referee_benefit_amount',
      headerName: 'Referee Benefit Amount',
      width: 150,
      type: 'number',
    },
  ];

  const getData = async () => {
    backdrop.handleBackdropOpen();

    try {
      const { data, error } = await supabase
        .schema('admin_backend')
        .from('campaigns_analytics_view')
        .select('*')
        .order('id', { ascending: true });

      if (error) {
        console.error('Error fetching analytics:', error);
        throw error;
      }

      setCampaigns(data);
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast.stack);
      snackbar.handleSnackbarOpen(errorCast.message, 'error');
    }

    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <PageBaseLayout
      title="Analytics"
      {...(authCtx.userRole === 'tranding_admin' && {
        actions: (
          <Button
            component={Link}
            to="./new"
            startIcon={<GridAddIcon />}
            size="small"
            variant="contained">
            New Campaign
          </Button>
        ),
      })}>
      <DataGrid
        columns={columns}
        rows={campaigns}
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            // whiteSpace normal to allow wrapping if header text is long
            whiteSpace: 'normal',
            lineHeight: '1.2',
          },
        }}
      />
      <Outlet />
    </PageBaseLayout>
  );
}
